import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Leaf, Smile, Tag, Cloud, Heart, ArrowRight, CheckCircle, History } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const CognitiveDefusionPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [thought, setThought] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [entries, setEntries] = useState([]);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/cognitive-defusion`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSession = async (exercise) => {
    try {
      const res = await axios.post(`${API}/cognitive-defusion`, {
        exercise_type: exercise.id,
        thought: thought,
      }, { withCredentials: true });
      toast.success(t("defusion.completed"));
      if (user?.role === "patient") promptShare(res.data, "cognitive_defusion");
      fetchEntries();
    } catch (e) { console.error(e); }
  };

  const DEFUSION_EXERCISES = [
    {
      id: "leaves",
      name: t("defusion.leaves"),
      description: t("defusion.leavesDesc"),
      icon: Leaf,
      instructions: [
        t("defusion.leaves.inst1"), t("defusion.leaves.inst2"), t("defusion.leaves.inst3"),
        t("defusion.leaves.inst4"), t("defusion.leaves.inst5"), t("defusion.leaves.inst6"),
      ],
    },
    {
      id: "silly",
      name: t("defusion.silly"),
      description: t("defusion.sillyDesc"),
      icon: Smile,
      instructions: [
        t("defusion.silly.inst1"), t("defusion.silly.inst2"), t("defusion.silly.inst3"),
        t("defusion.silly.inst4"), t("defusion.silly.inst5"),
      ],
    },
    {
      id: "prefix",
      name: t("defusion.prefix"),
      description: t("defusion.prefixDesc"),
      icon: Tag,
      instructions: [
        t("defusion.prefix.inst1"), t("defusion.prefix.inst2"), t("defusion.prefix.inst3"),
        t("defusion.prefix.inst4"), t("defusion.prefix.inst5"),
      ],
    },
    {
      id: "clouds",
      name: t("defusion.clouds"),
      description: t("defusion.cloudsDesc"),
      icon: Cloud,
      instructions: [
        t("defusion.clouds.inst1"), t("defusion.clouds.inst2"), t("defusion.clouds.inst3"),
        t("defusion.clouds.inst4"), t("defusion.clouds.inst5"),
      ],
    },
    {
      id: "thankMind",
      name: t("defusion.thankMind"),
      description: t("defusion.thankMindDesc"),
      icon: Heart,
      instructions: [
        t("defusion.thankMind.inst1"), t("defusion.thankMind.inst2"), t("defusion.thankMind.inst3"),
        t("defusion.thankMind.inst4"), t("defusion.thankMind.inst5"),
      ],
    },
  ];

  const closeExercise = (save = false) => {
    if (save && selectedExercise) saveSession(selectedExercise);
    setSelectedExercise(null);
    setThought("");
    setCurrentStep(0);
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="cognitive-defusion-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("defusion.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("defusion.pageSubtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("defusion.sessionsCompleted")}
            </span>
          )}
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.cognitive_defusion} />

        {selectedExercise ? (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedExercise.icon className="w-5 h-5" />
                {selectedExercise.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground">{selectedExercise.description}</p>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("defusion.yourThought")}</label>
                <Textarea
                  placeholder={t("defusion.thoughtPlaceholder")}
                  value={thought}
                  onChange={(e) => setThought(e.target.value)}
                  rows={2}
                  data-testid="thought-input"
                />
              </div>

              <div>
                <p className="text-sm font-medium mb-3">{t("defusion.followSteps")}</p>
                <ol className="space-y-3">
                  {selectedExercise.instructions.map((instruction, idx) => (
                    <li
                      key={idx}
                      className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                        currentStep >= idx ? "bg-teal-50" : "bg-muted/50"
                      }`}
                      onClick={() => setCurrentStep(idx)}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
                        currentStep >= idx ? "bg-teal-500 text-white" : "bg-muted text-muted-foreground"
                      }`}>
                        {currentStep > idx ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                      </span>
                      <span className={currentStep >= idx ? "text-foreground" : "text-muted-foreground"}>
                        {instruction}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <Button
                onClick={() =>
                  currentStep < selectedExercise.instructions.length - 1
                    ? setCurrentStep(currentStep + 1)
                    : closeExercise(true)
                }
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="next-step-btn"
              >
                {currentStep < selectedExercise.instructions.length - 1 ? (
                  <>
                    {t("compassion.nextStep")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  t("compassion.finishExercise")
                )}
              </Button>

              <Button variant="ghost" onClick={() => closeExercise(false)}>
                {t("defusion.backToExercises")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {DEFUSION_EXERCISES.map((exercise) => (
              <Card
                key={exercise.id}
                className="card-hover cursor-pointer"
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`defusion-card-${exercise.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                    <exercise.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{exercise.name}</h3>
                  <p className="text-sm text-muted-foreground">{exercise.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default CognitiveDefusionPage;
